<template>
  <div class="location-rental-places">
    <div class="add-row">
      <Multiselect
        v-model="selectedRentalPlaces"
        :options="availableRentalPlaces"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :show-labels="false"
        :custom-label="optionLabel"
        track-by="id"
        placeholder="Add support expenses…"
        class="rental-place-select"
      >
        <template #noResult>No support expenses match your search.</template>
        <template #noOptions>All support expenses already belong to this location.</template>
      </Multiselect>
      <button
        type="button"
        class="add-button"
        :disabled="!selectedRentalPlaces.length || isSaving"
        @click="addSelected"
      >
        <i class="bi bi-plus-lg"></i> Add
      </button>
    </div>

    <div v-if="isLoading" class="state-message">Loading support expenses…</div>

    <p v-else-if="!rentalPlaces.length" class="state-message">
      No support expenses at this location yet. Add existing ones above, or create one from
      <router-link :to="{ name: 'RentalPlaceList' }">Support Expenses</router-link>
      in the menu.
    </p>

    <table v-else class="rental-place-table">
      <thead>
        <tr>
          <th>Cost number</th>
          <th>Location</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="rentalPlace in rentalPlaces"
          :key="rentalPlace.id"
          class="rental-place-row"
          @click="$router.push({ name: 'RentalPlaceDetailPage', params: { id: rentalPlace.id } })"
        >
          <td>{{ rentalPlace.cost_number }}</td>
          <td>{{ locationNames[rentalPlace.location_id] || '—' }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import axios from 'axios';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { useToast } from 'vue-toastification';
import { API_ENDPOINTS } from '@/services/config';

export default {
  name: 'LocationRentalPlaces',
  components: { Multiselect },
  props: {
    locationId: {
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      allRentalPlaces: [],
      locationNames: {},
      selectedRentalPlaces: [],
      isLoading: false,
      isSaving: false,
    };
  },
  computed: {
    rentalPlaces() {
      return this.allRentalPlaces.filter((rentalPlace) => rentalPlace.location_id === Number(this.locationId));
    },
    availableRentalPlaces() {
      return this.allRentalPlaces.filter((rentalPlace) => rentalPlace.location_id !== Number(this.locationId));
    },
  },
  created() {
    this.fetchRentalPlaces();
    this.fetchLocationNames();
  },
  methods: {
    async fetchRentalPlaces() {
      this.isLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.rentalPlaces);
        this.allRentalPlaces = response.data;
      } catch (error) {
        console.error('Failed to fetch rental places:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchLocationNames() {
      try {
        const response = await axios.get(API_ENDPOINTS.locations);
        this.locationNames = Object.fromEntries(response.data.map((location) => [location.id, location.name]));
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    },
    optionLabel(rentalPlace) {
      const currentLocation = this.locationNames[rentalPlace.location_id];
      return currentLocation ? `${rentalPlace.cost_number} (${currentLocation})` : rentalPlace.cost_number;
    },
    async addSelected() {
      const count = this.selectedRentalPlaces.length;
      // A rental place belongs to exactly one location, so adding it here moves it.
      const isConfirmed = window.confirm(
        `Move ${count} support expense${count === 1 ? '' : 's'} to this location? `
        + 'They will be removed from their current location.'
      );
      if (!isConfirmed) return;

      this.isSaving = true;
      const results = await Promise.allSettled(
        this.selectedRentalPlaces.map((rentalPlace) =>
          axios.put(
            API_ENDPOINTS.rentalPlace(rentalPlace.id),
            { location_id: Number(this.locationId) },
            { headers: { 'Content-Type': 'application/json' } }
          ))
      );
      const failed = results.filter((result) => result.status === 'rejected');

      if (failed.length) {
        this.toast.error(failed[0].reason?.response?.data?.message || `Failed to add ${failed.length} support expense(s)`);
      }
      if (failed.length < count) {
        this.toast.success(`Added ${count - failed.length} support expense${count - failed.length === 1 ? '' : 's'}`);
      }

      this.selectedRentalPlaces = [];
      this.isSaving = false;
      await this.fetchRentalPlaces();
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.location-rental-places {
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .state-message {
    margin: 0;
    color: rgba($primary-color, 0.75);
  }

  .add-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 12px;

    .rental-place-select {
      flex: 1;
    }
  }

  .add-button {
    width: auto;
    margin: 0;
    padding: 8px 16px;
    min-height: 40px;
    background-color: $primary-color;
    color: #fff;
    border: none;
    border-radius: 5px;
    white-space: nowrap;
    cursor: pointer;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  }

  .rental-place-table {
    width: 100%;

    th {
      background-color: $form-color;
      border: 1px solid $primary-color;
    }
  }

  .rental-place-row {
    cursor: pointer;

    &:hover {
      background-color: $form-color;
    }
  }
}
</style>
